import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Check, Share2 } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import { useStore, type AllYouCanPlan, type Store, type Course } from '../../store/useStore'
import { showToast } from '../../components/Toast'
import { api, ApiError, getFirebaseIdToken } from '../../services/api'
import { useCoursesByStore } from '../../hooks/useCourses'
import { CANCEL_DEADLINE_HOURS, RESPONSE_DEADLINE_HOURS } from './reservationPolicy'
import { bookableRange } from '../../lib/bookableRange'
import CancellationPolicyTable from './CancellationPolicyTable'
import MonthCalendar from '../../components/MonthCalendar'
import StripeCheckout from '../../components/StripeCheckout'
import PayMethodToggle from '../../components/PayMethodToggle'
import AutoTopUpSheet from '../../components/AutoTopUpSheet'
import { topupBreakdownFor, topupParamsFor } from '../../lib/pointsTopup'
import { reservationPaymentRules, storePaymentOptions } from '../../lib/storePayment'
import ReservationShareSheet from '../../components/ReservationShareSheet'
import PushSetupCard from '../../components/PushSetupCard'
import { apiErrorI18nKey, apiErrorRef, isRetryable, paymentDeclineI18nKey } from '../../lib/apiErrorMessage'
import { forgetReservationRequestKey, reservationRequestKey } from '../../lib/reservationRequestKey'

// #1583 人数のチップを何個まで出すか。飲食店の 1 組は 1〜8 名が大半なので、
// そこはワンタップで選べるようにし、それ以上は直接入力へ逃がす。
// 以前は下限〜上限を全部ボタンで描画しており、店舗が上限を 130 にすると
// 129 個並んで「2 名」を選ぶのも遅くなっていた。
const CHIP_COUNT = 10

// #1357 入力ステップ。1 人数・コース → 2 日時 → 3 お客様情報 → 4 確認
// #1373 コースを先に選ぶ。空き枠はコース (availableDays / durationMinutes) で
// 変わるため、日時を先に決めさせるとコース変更のたびに選び直しになっていた。
type DetailStep = 1 | 2 | 3 | 4
const DETAIL_STEPS: DetailStep[] = [1, 2, 3, 4]

interface Props {
  /** #1246: ゲストにも空き枠の閲覧・入力までは開放し、予約確定の瞬間だけ
   * ログインを求める。復帰先 (postLoginRedirect) は呼び出し側が仕込む。
   * 未指定ならゲスト確定時はエラートースト (従来挙動)。 */
  onRequireLogin?: () => void
  open: boolean
  onClose: () => void
  store: Store
  // #737: optional course context. When provided, the modal uses the course's
  // party-size range, fetches slots for the course, and posts courseId.
  course?: Course | null
}

export default function ReservationModal({ open, onClose, store, course: initialCourse, onRequireLogin }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { user, isLoggedIn, wallet, restoreSession } = useStore()
  const config = store.reservationConfig

  // #737: 食べログ風の "コース選択" を予約モーダル内に組み込む。
  // initialCourse 指定 (CourseDetail からの導線) なら preselect、無指定なら null = 席のみ。
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(initialCourse ?? null)
  const { courses: availableCourses } = useCoursesByStore(store.id)

  // Course's own party-size range overrides the store-level one
  const minParty = selectedCourse?.partySizeRange.min ?? config?.partySizeRange.min ?? 1
  const maxParty = selectedCourse?.partySizeRange.max ?? config?.partySizeRange.max ?? 10
  // #2026 保存値 (config.paymentTiming) ではなく、この店で実際に使われる支払い方法。
  // 現金のみの店は前払いを保存していても当日払いになる (サーバが予約時に固定する)
  const { paymentTiming, noShowProtection } = reservationPaymentRules(store)

  // #1644 飲み放題プラン (任意)。course.drinkPlanIds が指す**実体の放題プラン
  // (#1353) を id で選択**する。名称・価格・説明の SSOT はプラン側 — コース側に
  // 複製させると実体と表示が必ずズレる (ユーザーレビュー指摘)
  const [drinkPlanId, setDrinkPlanId] = useState<string | null>(null)
  const [ayPlans, setAyPlans] = useState<AllYouCanPlan[]>([])
  // **取得失敗を「プラン無し」にしない。** ここを空にすると飲み放題を選べない
  // まま予約が成立し、**その分を含まない金額で確定してしまう** (unitPriceYen が
  // コース単体になる)。表示崩れではなく金額の問題なので、失敗は明示する。
  const [slotsFailed, setSlotsFailed] = useState<unknown>(null)
  const [slotsReloadKey, setSlotsReloadKey] = useState(0)
  const [ayPlansFailed, setAyPlansFailed] = useState<unknown>(null)
  const [ayPlansLoading, setAyPlansLoading] = useState(false)
  const [ayPlansReloadKey, setAyPlansReloadKey] = useState(0)
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setAyPlansFailed(null)
    setAyPlansLoading(true)
    api.listAllYouCanPlans(store.id)
      .then((r) => { if (!cancelled) setAyPlans(r.plans) })
      .catch((e) => {
        // **古い一覧を残さない。** モーダルは開閉で再マウントされないので、
        // 前回成功時のプランが残っていると「読み込めませんでした」と
        // 選択可能な一覧が同時に出てしまう。
        if (!cancelled) { setAyPlans([]); setAyPlansFailed(e) }
      })
      .finally(() => { if (!cancelled) setAyPlansLoading(false) })
    return () => { cancelled = true }
  }, [open, store.id, ayPlansReloadKey])
  const bookableDrinkPlans = (selectedCourse?.drinkPlanIds ?? [])
    .map((id) => ayPlans.find((p) => p.id === id))
    .filter((p): p is AllYouCanPlan => !!p && p.isActive !== false)
  const selectedDrinkPlan = bookableDrinkPlans.find((p) => p.id === drinkPlanId) ?? null
  // 1 人単価 = コース + 飲み放題。合計・キャンセル規定表示に使う (サーバと同じ式)
  const unitPriceYen = (selectedCourse?.priceYen ?? 0) + (selectedDrinkPlan?.priceYen ?? 0)

  const [selectedDate, setSelectedDate] = useState<string>('')
  const [selectedTime, setSelectedTime] = useState<string>('')
  // #1357 未選択で開始する。初期値が入っていると客が確認せず進み、
  // **意図しない人数で予約が確定する**（店にとっても実害がある）。
  const [partySize, setPartySize] = useState<number | null>(null)
  // #1583 「N名以上」を選ぶと直接入力に切り替える
  const [typingParty, setTypingParty] = useState(false)
  const [contactName, setContactName] = useState<string>(user?.name || '')
  const [contactEmail, setContactEmail] = useState<string>(user?.email || '')
  const [contactPhone, setContactPhone] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [slots, setSlots] = useState<string[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // #738 C-1/C-2: 2 段階フロー (details → payment) + 規約同意
  // #746: 完了 step で share sheet を強訴求するため 'complete' を追加
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  // #1804 支払い方法 (カード / YOKOSO 通貨) は独立した段にする。注文内容と
  // 同じ画面に積むと「日時と人数」と「何で払うか」という別種の質問が混ざる (#1805)
  const [step, setStep] = useState<'details' | 'method' | 'payment' | 'complete'>('details')
  const [payMethod, setPayMethod] = useState<'card' | 'points'>('card')
  // #1769 店の設定と feature flag をまとめて見る (判定表は lib/storePayment.ts)
  const { cardInApp: cardAllowed, points: pointsAllowed } = storePaymentOptions(store)
  const balance = wallet?.balanceJPYC ?? 0
  const [showAutoTopUp, setShowAutoTopUp] = useState(false)
  // 支払い待ちの予約。方法を選んでから実際に払う
  const [pendingReservationId, setPendingReservationId] = useState<string | null>(null)
  // #2577 仮押さえの期限 (ISO)。過ぎたらカードの画面で払わせない
  const [payBy, setPayBy] = useState<string | null>(null)
  const [pendingAmount, setPendingAmount] = useState(0)
  // #1357 入力を 4 段に分ける。以前は 1 画面に全部縦積みで、日付を選んでも
  // 時間欄が画面外にあり気づきにくかった。ステップなら次の操作が常に 1 つに絞られる。
  const [detailStep, setDetailStep] = useState<DetailStep>(1)
  const [paymentIntent, setPaymentIntent] = useState<{
    clientSecret: string
    paymentIntentId: string
    connectedAccountId: string
    amount: number
    captureMethod: 'automatic' | 'manual'
  } | null>(null)
  const [longTermFallbackApplied, setLongTermFallbackApplied] = useState(false)
  const [effectivePaymentTiming, setEffectivePaymentTiming] = useState<'prepaid' | 'deposit' | 'onsite' | null>(null)
  // #746: 予約成立後の share sheet 起動と shortCode 保持
  const [completedShortCode, setCompletedShortCode] = useState<string | null>(null)
  const [shareSheetOpen, setShareSheetOpen] = useState(false)

  // Reset form on open transition (derive-state-from-props pattern, avoids setState-in-useEffect).
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setSelectedCourse(initialCourse ?? null)
      setDrinkPlanId(null)
      setSelectedDate('')
      setSelectedTime('')
      setPartySize(null)
      setContactName(user?.name || '')
      setContactEmail(user?.email || '')
      setContactPhone('')
      setNotes('')
      setSlots([])
      // #738 C-1/C-2: 2 段階フロー state リセット
      setAgreedToTerms(false)
      setStep('details')
      setPayMethod('card')
      setShowAutoTopUp(false)
      setPendingReservationId(null)
      setPendingAmount(0)
      setDetailStep(1)
      setPaymentIntent(null)
      setLongTermFallbackApplied(false)
      setEffectivePaymentTiming(null)
      // #746: share state リセット
      setCompletedShortCode(null)
      setShareSheetOpen(false)
    }
  }

  // Clamp party size when course selection changes (course range may differ from store range).
  const [trackedCourseId, setTrackedCourseId] = useState<string | null>(selectedCourse?.id ?? null)
  if ((selectedCourse?.id ?? null) !== trackedCourseId) {
    setTrackedCourseId(selectedCourse?.id ?? null)
    setPartySize((p) => (p == null ? null : Math.max(minParty, Math.min(maxParty, p))))
    setSelectedTime('') // available slots may differ for the new course; force re-pick
    setDrinkPlanId(null) // #1644 プランはコース固有 (別コースの選択肢は別物)
    // #1373 コースは日時より前のステップなので、ここで巻き戻す必要はない。
    // 以前は日時が先だったため「戻る」しかなく、コースを変えるたびに
    // 日付から選び直しになっていた (#1371 で時間の空欄を塞いだ副作用)。
  }

  useEffect(() => {
    let cancelled = false
    const loadSlots = async () => {
      if (!selectedDate) {
        setSlots([])
        return
      }
      setLoadingSlots(true)
      try {
        // #1386 人数を渡して席数で絞り込む。渡さないと 1 名扱いになり、
        // 4 名では入れない枠が空いて見える。
        setSlotsFailed(null)
        const res = await api.getReservationSlots(
          store.id, selectedDate, selectedCourse?.id, partySize,
        )
        if (!cancelled) setSlots(res.slots || [])
      } catch (e) {
        console.error('[Reservation] slots load failed:', e)
        // **空き枠 0 件と区別する。** 一緒にすると「ご希望の日時は空きが
        // ありません」と出て、満席にしか見えない
        if (!cancelled) { setSlots([]); setSlotsFailed(e) }
      } finally {
        if (!cancelled) setLoadingSlots(false)
      }
    }
    loadSlots()
    return () => { cancelled = true }
    // #1386 partySize も依存に入れる。人数を変えたら空き枠を取り直さないと、
    // 4 名では入れない枠が選べたままになる（席数判定が客に届かない）。
  }, [selectedDate, store.id, selectedCourse?.id, partySize, slotsReloadKey])

  if (!open || !config) return null

  const { maxDate, isEnabled: isDateEnabled } = bookableRange({ ...config, paymentTiming }, selectedCourse)

  // #1357 各ステップの必須が埋まるまで「次へ」を押せない。
  // 埋まっていないまま進ませると、最後の確認で何が足りないか分からなくなる。
  // 飲み放題を持つコースで一覧の取得に失敗している間は進ませない。
  // 進めると**プラン分を含まない金額で予約が確定**し、prepaid では
  // その額で PaymentIntent まで作られる (#1624 の議論)。
  // **読み込み中も含める。** 失敗が届く前に進まれると、バナーを一度も見ないまま
  // コース単体の金額で予約が確定し、prepaid ではその額で PaymentIntent まで作られる
  const drinkPlansUnavailable =
    (!!ayPlansFailed || ayPlansLoading) && (selectedCourse?.drinkPlanIds ?? []).length > 0

  const canAdvance =
    detailStep === 1 ? partySize != null && !drinkPlansUnavailable
      : detailStep === 2 ? !!selectedDate && !!selectedTime
        : detailStep === 3 ? !!contactName.trim() && (!!contactEmail.trim() || !!contactPhone.trim())
          : true

  // #1386 席の終了予定 (予約時刻 + 店舗の滞在時間)。来店時刻からではなく
  // 予約時刻から数える — 遅刻分だけ後ろにずれると次の予約と衝突するため。
  const seatEndLabel = (() => {
    if (!selectedDate || !selectedTime || !config?.durationMinutes) return ''
    const end = new Date(
      new Date(`${selectedDate}T${selectedTime}:00+09:00`).getTime()
      + config.durationMinutes * 60_000,
    )
    return `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`
  })()

  const canSubmit =
    !!selectedDate &&
    !!selectedTime &&
    partySize != null &&
    partySize >= minParty &&
    partySize <= maxParty &&
    !!contactName.trim() &&
    (!!contactEmail.trim() || !!contactPhone.trim())

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    if (!agreedToTerms) {
      showToast({ type: 'error', message: t('courseReservation.mustAgreeTerms') })
      return
    }
    // #1246: 空き確認〜入力はゲスト可、確定 (POST /reservations = 認証必須 API)
    // の直前だけログインへ。バックエンドの deny はこれと独立に効いている。
    //
    // isLoggedIn (localStorage 永続) **だけでは足りない**: Firebase のセッションが
    // 切れてもこのフラグは true のまま残るため、トークン無しで POST まで進み、
    // 客には「アクセス権限がありません」としか出ないまま予約が取れなかった
    // (「人によって予約が失敗する」の正体)。最終判定は実トークンが取れるかで行う。
    if (!isLoggedIn || !(await getFirebaseIdToken())) {
      if (onRequireLogin) onRequireLogin()
      else showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    setSubmitting(true)
    try {
      const reservation = await api.createReservation({
        storeId: store.id,
        date: selectedDate,
        time: selectedTime,
        partySize,
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        notes: notes.trim() || undefined,
        courseId: selectedCourse?.id,
        // #1644 実体プランの id で指定 (名称・価格はサーバがプラン定義から引く)。
        // 単価のエコーは店主の同時価格編集時に 409 で止めてもらうための照合値
        drinkPlanId: selectedCourse && selectedDrinkPlan ? selectedDrinkPlan.id : undefined,
        drinkPlanPriceYen: selectedCourse && selectedDrinkPlan ? selectedDrinkPlan.priceYen : undefined,
        // #2190 項目 3 同じ内容の申し込みなら同じ鍵 → サーバは同じ予約を返す (送り直しで席を二重に押さえない)
        // 連絡先・要望も入れる (直して送り直したら別の申し込み。同じ鍵だと直す前の予約が返り、直した分が残らない)
        requestKey: reservationRequestKey({
          storeId: store.id, date: selectedDate, time: selectedTime, partySize,
          courseId: selectedCourse?.id ?? null,
          drinkPlanId: selectedCourse && selectedDrinkPlan ? selectedDrinkPlan.id : null,
          contactName: contactName.trim(), contactEmail: contactEmail.trim(),
          contactPhone: contactPhone.trim(), notes: notes.trim(),
        }),
      })
      // #738 C-1: 決済が必要な場合 (= captureMethod 設定済) は Stripe Elements 表示へ。
      // 不要 (= 当日払い保護なし / 席のみ) なら完了 step (#746) へ。
      if (reservation.captureMethod) {
        setLongTermFallbackApplied(!!reservation.longTermFallbackApplied)
        setEffectivePaymentTiming(reservation.paymentTiming ?? null)
        // shortCode は payment success 後に確定表示するため保持
        setCompletedShortCode(reservation.shortCode || null)
        setPendingReservationId(reservation.id)
        setPayBy(reservation.paymentHoldExpiresAt ?? null)
        setPendingAmount(
          reservation.paymentTiming === 'deposit'
            ? (reservation.depositAmount ?? 0)
            : (reservation.totalAmount ?? 0),
        )
        // #1804 前払い / 手付はポイントでも払える。**両方使えるときだけ聞く** —
        // 片方しか使えない店で選択肢 1 つの画面を挟むのは操作が増えるだけ (#1805)
        if (pointsAllowed && cardAllowed && reservation.paymentTiming !== 'onsite') {
          setStep('method')
        } else if (pointsAllowed && !cardAllowed && reservation.paymentTiming !== 'onsite') {
          setPayMethod('points')
          await payWithPoints(reservation.id)
        } else {
          // #2190 決済の準備の失敗は、予約の送信の失敗と分けて出す (予約はもうできている)
          await prepareCardPayment(reservation.id)
        }
      } else {
        forgetReservationRequestKey()
        setCompletedShortCode(reservation.shortCode || null)
        setStep('complete')
      }
    } catch (e) {
      console.error('[Reservation] submit failed:', e)
      // 上のガードを抜けた後にトークンが失効するとサーバが 401/403 を返す。
      // 「アクセス権限がありません」では客に何をすればいいか伝わらないので、
      // ログイン導線に戻す (認証以外の失敗は従来どおりのエラー表示)。
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        if (onRequireLogin) onRequireLogin()
        else showToast({ type: 'error', message: t('cart.loginRequired') })
        return
      }
      // 店側の手付金設定が 0 円のまま (設定不備)。「時間をおいて再試行」ではなく、
      // 客にできることはお店への連絡しかないと伝える
      if (e instanceof ApiError && e.code === 'deposit_not_configured') {
        showToast({ type: 'error', message: t('storeDetail.reservationStoreConfigError') })
        return
      }
      // #1644 店主がコースの飲み放題を同時編集した / プランが消えた。
      // 見せた金額と違う課金はサーバが止めるので、客には選び直してもらう
      if (
        e instanceof ApiError &&
        ['drink_plan_changed', 'drink_plan_unavailable', 'drink_plan_not_bookable', 'unknown_drink_plan'].includes(e.code ?? '')
      ) {
        setDrinkPlanId(null)
        setDetailStep(1)
        showToast({ type: 'error', message: t('courseReservation.drinkPlanChanged') })
        return
      }
      showToast({ type: 'error', message: t('storeDetail.reservationFailed') })
    } finally {
      setSubmitting(false)
    }
  }

  // #1804 ポイントで払う。カードと違って与信が無いのでその場で引かれ、
  // 成功したらそのまま完了 (Stripe の確認画面に相当する段が無い)。
  const payWithPoints = async (
    reservationId: string,
    topup?: { topupAmount: number; topupFeeJpyc: number },
  ) => {
    setSubmitting(true)
    const complete = async () => {
      // #1804 残高を取り直す。**古いまま持ち回ると次の支払いで不足判定を誤り**、
      // チャージシートを出さずに 400 で詰まる (他のポイント導線と同じ扱い)
      await restoreSession().catch(() => { /* 表示だけの問題。決済は成立している */ })
      showToast({ type: 'success', message: t('courseReservation.paymentComplete') })
      forgetReservationRequestKey()
      setStep('complete')
    }
    try {
      await api.reservationPointsCheckout(reservationId, topup)
      await complete()
    } catch (e) {
      console.error('[Reservation] points checkout failed:', e)
      // #2190 前の送信の支払いが通っていた (応答だけ届かなかった)。もう一度払わせない。
      // already_paid は、同時に 2 回届いて 2 回目を戻したとき (1 回目は通っている)
      if (e instanceof ApiError && ['reservation_payment_already_made', 'already_paid'].includes(e.code ?? '')) {
        await complete()
        return
      }
      // #2577 仮押さえの期限が過ぎた (席はもう空いている)。申し込み直しを案内する
      if (e instanceof ApiError && e.code === 'reservation_payment_expired') {
        showToast({ type: 'error', message: t('courseReservation.paymentExpired') })
        return
      }
      // #2190 予約はもうできている。送り直しても予約は二重にならない (同じ申し込みの鍵)。
      // 以前の apiErrorI18nKey は必ず文言を返すので、400 でも「サーバで問題が発生」と出ていた
      showToast({
        type: 'error',
        message: t(paymentDeclineI18nKey(e) ?? 'storeDetail.reservationPaymentFailed'),
      })
    } finally {
      setSubmitting(false)
    }
  }

  // #2190 項目 3 カードの決済の準備。**予約は既に作られている**ので PI を作るだけ。失敗しても予約は作り直さない
  // (送り直しは同じ申し込みの鍵で同じ予約を返し、サーバは作り済みの PI を使い回す)
  // 'ready' = カードの入力へ進んだ / 'done' = もう払われていた (完了にした) / 'failed' = 案内を出した
  const prepareCardPayment = async (reservationId: string): Promise<'ready' | 'done' | 'failed'> => {
    try {
      const pi = await api.createReservationPaymentIntent(reservationId)
      setPaymentIntent(pi)
      setStep('payment')
      return 'ready'
    } catch (e) {
      console.error('[Reservation] payment intent failed:', e)
      if (e instanceof ApiError && e.code === 'reservation_payment_already_made') {
        // 与信はもう通っている (前の送信のカードの入力が済んでいた)。完了として扱う
        void handlePaymentSuccess()
        return 'done'
      }
      // #2577 仮押さえの期限が過ぎた (席はもう空いている)。申し込み直しを案内する
      if (e instanceof ApiError && e.code === 'reservation_payment_expired') {
        showToast({ type: 'error', message: t('courseReservation.paymentExpired') })
        return 'failed'
      }
      // #2577 お店が承認し、当日払いに回した予約
      if (e instanceof ApiError && e.code === 'reservation_pay_onsite') {
        showToast({ type: 'info', message: t('courseReservation.payOnsiteInstead') })
        return 'failed'
      }
      showToast({
        type: 'error',
        message: t(paymentDeclineI18nKey(e) ?? 'storeDetail.reservationPaymentFailed'),
      })
      return 'failed'
    }
  }

  // カードへ切り替える (支払い方法を選ぶ段から)
  const payWithCard = async (reservationId: string) => {
    setSubmitting(true)
    try {
      await prepareCardPayment(reservationId)
    } finally {
      setSubmitting(false)
    }
  }

  // #2577 カードの成功をサーバに伝える (webhook を待たずに予約を与信済みにする)。結果を返す。確かめられなければ null —
  // そのときは webhook と巡回 (仮押さえの期限切れの前に Stripe を見る) が同じ手続きで拾う。
  // 通信の一時的な失敗に備えて 2 回まで送り直す
  const confirmCardPayment = async (reservationId: string) => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        return await api.confirmReservationPayment(reservationId)
      } catch (e) {
        if (e instanceof ApiError && e.status < 500) return null   // 本人でない等。送り直しても変わらない
        if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 2000 * (attempt + 1)))
      }
    }
    return null
  }

  // #738 C-1: Stripe 決済成功時 (= 予約成立 + カード confirm 完了)
  // #746: 完了 step に遷移してシェア訴求 (= my-reservations への自動遷移はしない)
  const showCompleted = () => {
    showToast({ type: 'success', message: t('courseReservation.paymentComplete') })
    forgetReservationRequestKey()
    setStep('complete')
  }

  // #2577 確かめた結果を見てから完了を出す。予約に反映されなかった (払ったのが予約に結び付いていない決済だった・
  // お店が先に断った・期限切れになった) なら、そう出してマイ予約へ (与信はサーバが自動で解放する)。
  // まだ未払いに見えるだけ (処理中・ほかの書き手と競った) は完了を出す (知らせと巡回が与信済みにする)。
  // 確かめられなければ完了を出す
  const handlePaymentSuccess = async (paidIntentId?: string) => {
    const r = pendingReservationId ? await confirmCardPayment(pendingReservationId) : null
    const notApplied = !!r && (
      ['expired', 'cancelled', 'rejected'].includes(r.status)
      || (!!paidIntentId && !!r.paymentIntentId && r.paymentIntentId !== paidIntentId)
    )
    if (notApplied) {
      showToast({ type: 'error', message: t('courseReservation.paymentNotApplied') })
      onClose()
      navigate('/my-reservations', { replace: true })
      return
    }
    showCompleted()
  }

  // #2577 カードの決済が取り消されていた。ほかのタブでポイントで払っていれば (払う前にカードの決済を閉じる) 完了。
  // そうでなければ仮押さえ切れで、カードの部品が「期限が過ぎた」と出す
  // #2577 端末の時計では仮押さえの期限を過ぎた: サーバの時計でまだ期限内か、お店がもう承認していれば (当日払い +
  // 保証のカードを入れ直すなど)、まだ払われていない限り払ってよい。申し込み中で期限切れなら止める (席はもう空いている)
  const checkPayableAfterHold = async (): Promise<boolean> => {
    if (!pendingReservationId) return false
    const r = await api.confirmReservationPayment(pendingReservationId)
    const unpaid = r.paymentStatus === 'pending' || r.paymentStatus === 'failed'
    return unpaid && (r.status === 'approved' || (r.status === 'pending' && r.holdExpired === false))
  }

  const checkCanceledPayment = async (): Promise<string | null> => {
    const expired = t('courseReservation.paymentExpired')
    if (!pendingReservationId) return expired
    const r = await api.confirmReservationPayment(pendingReservationId)
    if (r.paymentStatus === 'paid' || r.paymentStatus === 'authorized') {
      showCompleted()
      return null
    }
    // お店が先に承認し、当日払いに回した (承認の前に払われていなかった)。予約は生きている
    if (r.status === 'approved' && r.paymentStatus === 'capture_failed') {
      return t('courseReservation.payOnsiteInstead')
    }
    // 予約は生きていて未払いで、仮押さえの期限にも間に合う (ほかのタブのポイント払いが通らなかった等) →
    // 決済を作り直して入れ直してもらう (取り消された決済では払えない)。当日払いに回した (capture_failed) 予約などは
    // 作り直さない (払っても使われず、解放される)
    const unpaid = r.paymentStatus === 'pending' || r.paymentStatus === 'failed'
    const inTime = r.status === 'approved' || !payBy || Date.now() <= Date.parse(payBy)   // 承認済みは仮押さえと関係ない
    if (unpaid && (r.status === 'pending' || r.status === 'approved') && inTime) {
      // 作り直せなければ、カードの画面に失敗を出す (処理中のまま止めない)。もう払われていた (done) なら完了にした
      const prepared = await prepareCardPayment(pendingReservationId)
      if (prepared === 'failed') return t('storeDetail.reservationPaymentFailed')
      if (prepared === 'ready') showToast({ type: 'info', message: t('courseReservation.cardPaymentReset') })
      return null
    }
    return expired
  }

  // 客がカード入力を中断 → 予約 doc は残す (= paymentStatus='pending')。
  // #2190 マイ予約から払い直す手段は無く、仮押さえは 15 分で切れる。案内もそのとおりにする (paymentDeferred)
  const handlePaymentCancel = () => {
    showToast({ type: 'info', message: t('courseReservation.paymentDeferred') })
    onClose()
    navigate('/my-reservations', { replace: true })
  }

  // #746: 完了 step — 予約成立サマリー + シェア訴求
  if (step === 'complete') {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
        {/* #1990 通知の案内 (iPhone は 3 ステップ) が乗るので、他の step と同じく
          * 小さい画面では中でスクロールさせる */}
        <div className="w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 animate-scale-in">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-bold text-text-primary">
              {t('reservationShare.completedTitle')}
            </h3>
            <button
              onClick={() => {
                onClose()
                navigate('/my-reservations', { replace: true })
              }}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
              aria-label="close"
            >
              <X size={16} className="text-text-secondary" />
            </button>
          </div>

          <div className="bg-matcha/10 rounded-xl p-4 mb-4">
            <p className="font-display font-bold text-text-primary mb-1">{store.name}</p>
            <p className="font-body text-sm text-text-primary">
              {selectedDate} {selectedTime} · {partySize}名
              {selectedCourse && (
                <>
                  <br />
                  <span className="text-xs text-text-secondary">{selectedCourse.name}</span>
                </>
              )}
            </p>
          </div>

          <p className="font-body text-xs text-text-secondary mb-4 leading-relaxed">
            {t('reservationShare.completedHint')}
          </p>

          {/* #1990 承認は push で届く (#1989)。「承認されたらお知らせします」と言える
            * この場面が、通知を許可 / ホーム画面に追加してもらう一番自然な入口 */}
          <div className="mb-4">
            <PushSetupCard context="reservation" />
          </div>

          <button
            onClick={() => setShareSheetOpen(true)}
            className="tap-effect flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-yokoso-red font-display text-sm font-semibold text-white mb-2.5"
          >
            <Share2 size={16} />
            {t('reservationShare.shareNow')}
          </button>
          <button
            onClick={() => {
              onClose()
              navigate('/my-reservations', { replace: true })
            }}
            className="tap-effect flex w-full h-12 items-center justify-center rounded-xl border border-gray-200 bg-white font-display text-sm font-semibold text-text-primary"
          >
            {t('reservationShare.goToMyReservations')}
          </button>
        </div>

        <ReservationShareSheet
          open={shareSheetOpen}
          onClose={() => setShareSheetOpen(false)}
          reservation={{
            storeName: store.name,
            storeAddress: store.address || undefined,
            date: selectedDate,
            time: selectedTime,
            partySize: partySize ?? 0,
            courseName: selectedCourse?.name,
            paymentTiming: effectivePaymentTiming ?? paymentTiming,
            shortCode: completedShortCode || undefined,
          }}
        />
      </div>
    )
  }

  // #738 C-1: payment step (Stripe Elements 表示) — details step とは完全に別レイアウト
  // #1804 支払い方法。**「いつ・何人で」とは別の質問**なので画面を分ける (#1805)
  if (step === 'method' && pendingReservationId) {
    const usePoints = payMethod === 'points'
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
        <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[90vh] overflow-y-auto animate-scale-in">
          <h3 className="font-display text-lg font-bold text-text-primary mb-1">
            {t('checkout.methodTitle')}
          </h3>
          <p className="font-body text-xs text-text-tertiary mb-4">
            ¥{pendingAmount.toLocaleString()} — {store.name}
          </p>

          <PayMethodToggle
            method={payMethod}
            onChange={setPayMethod}
            cardAllowed={cardAllowed}
            balance={balance}
            amount={pendingAmount}
            showTitle={false}
          />

          {/* #1804 **ポイントは予約時に引かれる。** カードは与信なので店が承認する
              まで金が動かないが、ポイントに与信は無い。承認を待つ間ずっと残高が
              減っているので、戻る条件を先に言っておく */}
          {usePoints && (
            <p className="mt-2 rounded-lg bg-matcha/10 border border-matcha/20 px-3 py-2 font-body text-[11px] leading-relaxed text-text-secondary">
              {t('courseReservation.pointsRefundNote', { hours: String(RESPONSE_DEADLINE_HOURS) })}
            </p>
          )}

          <button
            onClick={() => {
              if (!usePoints) return void payWithCard(pendingReservationId)
              // 残高が足りなければ、不足分のチャージを承認してもらってから払う
              if (balance < pendingAmount) return setShowAutoTopUp(true)
              void payWithPoints(pendingReservationId)
            }}
            disabled={submitting}
            className="mt-2 w-full h-12 rounded-xl bg-yokoso-red font-display text-base font-semibold text-white tap-effect disabled:opacity-50"
          >
            {t('checkout.payNow')}
          </button>

          {/* 中断しても予約 doc は残る (paymentStatus='pending')。
              客は my-reservations から後で払える */}
          <button
            onClick={handlePaymentCancel}
            className="mt-2 w-full h-11 rounded-xl border border-gray-200 font-body text-sm text-text-secondary tap-effect"
          >
            {t('courseReservation.payLater')}
          </button>
        </div>

        <AutoTopUpSheet
          open={showAutoTopUp}
          breakdown={topupBreakdownFor(balance, pendingAmount)}
          storeName={store.name}
          onCancel={() => setShowAutoTopUp(false)}
          onConfirm={async () => {
            setShowAutoTopUp(false)
            await payWithPoints(
              pendingReservationId, topupParamsFor(balance, pendingAmount),
            )
          }}
        />
      </div>
    )
  }

  if (step === 'payment' && paymentIntent) {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
        <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[90vh] overflow-y-auto animate-scale-in">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg font-bold text-text-primary">
              {t('courseReservation.paymentStepTitle')}
            </h3>
          </div>

          {/* #738 C-2: 8 日以上先で fallback 発動した場合の通知 */}
          {longTermFallbackApplied && effectivePaymentTiming && (
            <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5">
              <p className="font-body text-xs text-amber-900 leading-relaxed">
                ℹ️ {t('courseReservation.fallbackApplied', {
                  mode: t(`courseReservation.modeLabel.${effectivePaymentTiming}`),
                })}
              </p>
            </div>
          )}

          <StripeCheckout
            // 決済を作り直したら作り直す (Stripe の部品は同じまま clientSecret を変えられない)
            key={paymentIntent.paymentIntentId}
            clientSecret={paymentIntent.clientSecret}
            amount={paymentIntent.amount}
            storeName={store.name}
            connectedAccountId={paymentIntent.connectedAccountId}
            paymentIntentId={paymentIntent.paymentIntentId}
            storeId={store.id}
            captureMethod={paymentIntent.captureMethod}
            // #2190 予約の与信の文言 (以前は出前の「調理を開始した瞬間に決済」が出ていた)
            holdKind="reservation"
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
            payBy={payBy}
            canPayAfterHold={checkPayableAfterHold}
            onIntentCanceled={checkCanceledPayment}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[85vh] overflow-y-auto animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-text-primary">
            {t('storeDetail.reservationTitle')}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect">
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        {/* #1357 いま何段目かを常に見せる。全部縦積みだった頃は「あとどれだけ
            入力が要るか」が分からず、途中離脱の原因になっていた。 */}
        <div className="flex items-center gap-1.5 mb-4">
          {DETAIL_STEPS.map((n) => (
            <div
              key={n}
              className={`h-1 flex-1 rounded-full transition-colors ${
                n <= detailStep ? 'bg-navy' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className="font-body text-xs text-text-tertiary mb-3">
          {t(`reservationStep.label.${detailStep}`)}
        </p>

        {/* #1357 ステップ2: 日時 (#1373 でコースの後ろへ) */}
        {detailStep === 2 && (
        <>
        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('storeDetail.reservationDate')}
        </p>
        <div className="mb-4">
          <MonthCalendar
            value={selectedDate}
            onChange={(d) => { setSelectedDate(d); setSelectedTime('') }}
            maxDate={maxDate}
            isEnabled={isDateEnabled}
          />
        </div>

        {/* Time slots */}
        {selectedDate && (
          <>
            <p className="font-body text-sm font-semibold text-text-primary mb-2">
              {t('storeDetail.reservationTime')}
            </p>
            {loadingSlots ? (
              <p className="text-xs text-text-tertiary font-body mb-4">...</p>
            ) : slotsFailed ? (
              <div className="mb-4 flex flex-col items-start gap-1">
                <p className="font-body text-xs text-text-secondary">{t(apiErrorI18nKey(slotsFailed))}</p>
                <p className="font-body text-[10px] text-text-tertiary">{apiErrorRef(slotsFailed)}</p>
                {isRetryable(slotsFailed) && (
                  <button
                    onClick={() => setSlotsReloadKey((k) => k + 1)}
                    className="mt-1 rounded-lg bg-navy px-4 py-1.5 font-body text-xs text-white tap-effect"
                  >
                    {t('common.retry')}
                  </button>
                )}
              </div>
            ) : slots.length === 0 ? (
              <p className="text-xs text-text-tertiary font-body mb-4">{t('storeDetail.reservationNoSlots')}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {slots.map(s => (
                  <button
                    key={s}
                    onClick={() => setSelectedTime(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium transition-colors tap-effect ${
                      selectedTime === s ? 'bg-navy text-white' : 'bg-gray-100 text-text-primary'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        </>
        )}

        {/* #1357 ステップ1: 人数とコース (#1373 で日時より前へ) */}
        {detailStep === 1 && (
        <>
        {/* #737: コース選択 (食べログ風) — 「席のみ」+ 各アクティブコース */}
        {availableCourses.length > 0 && (
          <>
            <p className="font-body text-sm font-semibold text-text-primary mb-2">
              {t('courseReservation.selectCourse')}
            </p>
            <div className="space-y-2 mb-4">
              <CourseChoiceCard
                title={t('courseReservation.noCourse')}
                subtitle={t('courseReservation.noCourseDesc')}
                selected={selectedCourse === null}
                onClick={() => setSelectedCourse(null)}
              />
              {availableCourses.map((c) => (
                <CourseChoiceCard
                  key={c.id}
                  title={c.name}
                  subtitle={`¥${c.priceYen.toLocaleString()} / ${t('courseReservation.perPerson')} ・ ${c.durationMinutes}${t('courseReservation.durationUnit')}`}
                  selected={selectedCourse?.id === c.id}
                  onClick={() => setSelectedCourse(c)}
                />
              ))}
            </div>
          </>
        )}

        {/* 取得失敗を「プラン無し」と区別する。黙って選択肢を消すと、
            飲み放題を選べないまま予約が成立し、その分を含まない金額で
            確定してしまう (unitPriceYen がコース単体になる)。 */}
        {selectedCourse && ayPlansFailed && (selectedCourse.drinkPlanIds ?? []).length > 0 && (
          <div className="mb-4 rounded-lg border border-yokoso-red/30 bg-yokoso-red/5 p-3">
            <p className="font-body text-sm font-semibold text-text-primary">
              {t('courseReservation.drinkPlanTitle')}
            </p>
            <p className="mt-1 font-body text-xs text-text-secondary">{t(apiErrorI18nKey(ayPlansFailed))}</p>
            <p className="font-body text-[10px] text-text-tertiary">{apiErrorRef(ayPlansFailed)}</p>
            {isRetryable(ayPlansFailed) && (
              <button
                onClick={() => setAyPlansReloadKey((k) => k + 1)}
                disabled={ayPlansLoading}
                className="mt-2 rounded-lg bg-navy px-4 py-1.5 font-body text-xs text-white tap-effect disabled:opacity-50"
              >
                {ayPlansLoading ? t('common.loading') : t('common.retry')}
              </button>
            )}
          </div>
        )}

        {/* #1644: 飲み放題プラン (任意)。実体の放題プラン (#1353) から選ぶ —
            名称・価格・制限時間はプラン定義そのもの。来店マークで卓へ自動適用
            されるので、当日ドリンクの二重課金は起きない */}
        {selectedCourse && bookableDrinkPlans.length > 0 && (
          <>
            <p className="font-body text-sm font-semibold text-text-primary mb-2">
              {t('courseReservation.drinkPlanTitle')}
            </p>
            <div className="space-y-2 mb-4">
              <CourseChoiceCard
                title={t('courseReservation.drinkPlanNone')}
                subtitle={t('courseReservation.drinkPlanNoneDesc')}
                selected={drinkPlanId === null}
                onClick={() => setDrinkPlanId(null)}
              />
              {bookableDrinkPlans.map((plan) => (
                <CourseChoiceCard
                  key={plan.id}
                  title={plan.name}
                  subtitle={`+¥${plan.priceYen.toLocaleString()} / ${t('courseReservation.perPerson')} ・ ${plan.durationMinutes}${t('courseReservation.durationUnit')}${
                    plan.description ? ` ・ ${plan.description}` : ''
                  }`}
                  selected={drinkPlanId === plan.id}
                  onClick={() => setDrinkPlanId(plan.id)}
                />
              ))}
            </div>
          </>
        )}

        {selectedCourse && (
          <div className="mb-4 bg-yokoso-red/5 rounded-lg p-3">
            <p className="font-body text-xs text-text-secondary">
              {t('courseReservation.priceLabel')}: ¥{selectedCourse.priceYen.toLocaleString()} / {t('courseReservation.perPerson')}
            </p>
            {selectedDrinkPlan && (
              <p className="font-body text-xs text-text-secondary mt-0.5">
                {selectedDrinkPlan.name}: +¥{selectedDrinkPlan.priceYen.toLocaleString()} / {t('courseReservation.perPerson')}
              </p>
            )}
            <p className="font-body text-xs text-text-secondary mt-0.5">
              {t('courseReservation.totalLabel')}: ¥{(unitPriceYen * (partySize ?? 1)).toLocaleString()}
            </p>
            {paymentTiming !== 'onsite' && (
              <p className="font-body text-xs text-yokoso-red mt-1.5">
                {paymentTiming === 'prepaid'
                  ? t('courseReservation.paymentPrepaidNotice')
                  : t('courseReservation.paymentDepositNotice')}
              </p>
            )}
          </div>
        )}

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('storeDetail.reservationPartySize')} ({minParty}-{maxParty})
        </p>
        {/* 未選択で開始する。初期値が入っていると客が確認せず進み、
            意図しない人数で予約が確定する。

            #1583 以前は minParty〜maxParty を**全部ボタンで描画**していた。
            店舗が上限を 130 にすると 129 個並び、いちばん多い「2 名」を選ぶのにも
            スクロールが要る = **設定値で UI が壊れる**状態だった。
            よく使う範囲だけチップで出し、それ以外は直接入力に逃がす。 */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {Array.from(
            { length: Math.max(0, Math.min(maxParty, minParty + CHIP_COUNT - 1) - minParty + 1) },
            (_, i) => minParty + i,
          ).map((n) => (
            <button
              key={n}
              onClick={() => { setPartySize(n); setTypingParty(false) }}
              className={`w-11 h-11 rounded-lg font-body text-sm font-medium transition-colors ${
                partySize === n && !typingParty
                  ? 'bg-navy text-white font-bold'
                  : 'bg-gray-100 text-text-primary tap-effect'
              }`}
            >
              {n}
            </button>
          ))}
          {maxParty > minParty + CHIP_COUNT - 1 && (
            <button
              onClick={() => setTypingParty(true)}
              className={`h-11 px-3 rounded-lg font-body text-sm font-medium transition-colors ${
                typingParty ? 'bg-navy text-white font-bold' : 'bg-gray-100 text-text-primary tap-effect'
              }`}
            >
              {t('storeDetail.reservationPartyMore', { n: String(minParty + CHIP_COUNT) })}
            </button>
          )}
        </div>
        {typingParty && (
          <input
            type="number"
            inputMode="numeric"
            autoFocus
            min={minParty}
            max={maxParty}
            value={partySize ?? ''}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10)
              setPartySize(Number.isFinite(n) ? Math.min(Math.max(n, minParty), maxParty) : null)
            }}
            placeholder={`${minParty}-${maxParty}`}
            className="w-full h-11 rounded-lg border-2 border-navy px-3 font-body text-sm outline-none mb-4"
          />
        )}
        {!typingParty && <div className="mb-2" />}
        </>
        )}

        {/* #1357 ステップ3: お客様情報 */}
        {detailStep === 3 && (
        <>
        {/* Contact */}
        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('storeDetail.reservationContactName')}
        </p>
        <input
          type="text"
          value={contactName}
          onChange={e => setContactName(e.target.value)}
          className="w-full bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 mb-3"
        />

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('storeDetail.reservationContactEmail')}
        </p>
        <input
          type="email"
          value={contactEmail}
          onChange={e => setContactEmail(e.target.value)}
          className="w-full bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 mb-3"
        />

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('storeDetail.reservationContactPhone')}
        </p>
        <input
          type="tel"
          value={contactPhone}
          onChange={e => setContactPhone(e.target.value)}
          className="w-full bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 mb-4"
        />

        {/* Notes */}
        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('storeDetail.reservationNotes')}
        </p>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={t('storeDetail.reservationNotesHint')}
          rows={3}
          className="w-full bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 mb-4 resize-none"
        />

        </>
        )}

        {/* #1357 ステップ4: 確認。ここで初めて内容サマリと規定・同意を出す。
            入力途中で規定を見せても読まれないし、入力欄に埋もれる。 */}
        {detailStep === 4 && (
        <>
        <div className="mb-4 rounded-xl border border-gray-100 divide-y divide-gray-100">
          <SummaryRow label={t('storeDetail.reservationDate')} value={selectedDate} />
          <SummaryRow label={t('storeDetail.reservationTime')} value={selectedTime} />
          {/* #1386 席の利用時間を**予約する前に**伝える。伝えていないと、
            * 次の予約が入っているときに「そろそろ」と言いにくくなる。 */}
          {config?.durationMinutes ? (
            <SummaryRow
              label={t('storeDetail.reservationSeatTime')}
              value={t('storeDetail.reservationSeatTimeValue', {
                n: String(config.durationMinutes),
                until: seatEndLabel,
              })}
            />
          ) : null}
          <SummaryRow
            label={t('storeDetail.reservationPartySize')}
            value={t('storeDetail.reservationPartyValue', { count: String(partySize ?? 0) })}
          />
          <SummaryRow
            label={t('courseReservation.selectCourse')}
            value={selectedCourse?.name || t('courseReservation.noCourse')}
          />
          <SummaryRow label={t('storeDetail.reservationContactName')} value={contactName} />
          {selectedDrinkPlan && (
            <SummaryRow
              label={t('courseReservation.drinkPlanTitle')}
              value={`${selectedDrinkPlan.name} (+¥${selectedDrinkPlan.priceYen.toLocaleString()} / ${t('courseReservation.perPerson')})`}
            />
          )}
          {selectedCourse && (
            <SummaryRow
              label={t('courseReservation.totalLabel')}
              value={`¥${(unitPriceYen * (partySize ?? 0)).toLocaleString()}`}
            />
          )}
        </div>

        {/* #737: キャンセル料規定 (決済モード別、客が予約前に確認できるよう表示)
          *
          * #1371 席のみ予約 (コースなし) では出さない。前払い/手付金はコース料金に
          * かかるもので、席だけの予約では金銭が動かない。**発生しない規定を読ませると
          * 「席だけでもキャンセル料がかかる」と誤解される**。
          * 席のみのキャンセル条件は下の「予約ポリシー」で伝わる。 */}
        {selectedCourse && (
        <div className="mb-4">
          <CancellationPolicyTable
            variant="modal"
            highlightedMode={paymentTiming}
            onsiteProtected={noShowProtection}
            // #1371 この予約の実額でキャンセル規定を出す (#1644 飲み放題込み)
            totalAmount={unitPriceYen * (partySize ?? 0)}
          />
        </div>
        )}

        {/* Policy notice */}
        <p className="text-xs text-text-tertiary font-body leading-relaxed mb-4 bg-gray-50 rounded p-2.5">
          {t('storeDetail.reservationPolicy')}
          <br />
          <br />
          {t('storeDetail.reservationDeadlinePolicy', {
            hours: String(RESPONSE_DEADLINE_HOURS),
            cancelHours: String(CANCEL_DEADLINE_HOURS),
          })}
        </p>

        {/* #738 C-2: 規約同意 checkbox (必須) */}
        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-0.5 w-4 h-4 shrink-0 accent-yokoso-red"
          />
          <span className="font-body text-xs text-text-secondary leading-relaxed">
            {t('courseReservation.agreeToTerms')}
          </span>
        </label>

        {/* #738 C-2: 何が disabled 原因か明示 (= UX hint) */}
        {!submitting && (!canSubmit || !agreedToTerms) && (
          <p className="font-body text-xs text-yokoso-red mb-3 leading-relaxed">
            {!agreedToTerms
              ? `⚠️ ${t('courseReservation.mustAgreeTerms')}`
              : `⚠️ ${t('courseReservation.fillRequiredFields')}`}
          </p>
        )}

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting || !agreedToTerms}
          className={`w-full h-12 rounded-xl font-display font-bold text-sm transition-colors ${
            !canSubmit || submitting || !agreedToTerms ? 'bg-gray-300 text-white cursor-not-allowed' : 'bg-yokoso-red text-white tap-effect'
          }`}
        >
          {/* カード入力が必要なフロー (= card_required) では「次へ進む」と明示 */}
          {paymentTiming !== 'onsite' || (selectedCourse && noShowProtection)
            ? t('courseReservation.proceedToPayment')
            : t('storeDetail.reservationSubmit')}
        </button>
        </>
        )}

        {/* #1357 ステップ移動。最終ステップだけ確定ボタン（上）を出す。 */}
        {detailStep < 4 && (
          <div className="flex gap-2">
            {detailStep > 1 && (
              <button
                onClick={() => setDetailStep((n) => (n - 1) as DetailStep)}
                className="flex-1 h-12 rounded-xl bg-gray-100 font-display font-semibold text-sm text-text-secondary tap-effect"
              >
                {t('reservationStep.back')}
              </button>
            )}
            <button
              onClick={() => setDetailStep((n) => (n + 1) as DetailStep)}
              disabled={!canAdvance}
              className={`flex-[2] h-12 rounded-xl font-display font-bold text-sm transition-colors ${
                canAdvance ? 'bg-navy text-white tap-effect' : 'bg-gray-300 text-white cursor-not-allowed'
              }`}
            >
              {t('reservationStep.next')}
            </button>
          </div>
        )}
        {detailStep === 4 && (
          <button
            onClick={() => setDetailStep(3)}
            className="w-full mt-2 h-11 rounded-xl bg-gray-100 font-display font-semibold text-sm text-text-secondary tap-effect"
          >
            {t('reservationStep.back')}
          </button>
        )}
      </div>
    </div>
  )
}

function CourseChoiceCard({
  title,
  subtitle,
  selected,
  onClick,
}: {
  title: string
  subtitle: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-2 text-left rounded-lg border px-3 py-2.5 tap-effect transition-colors ${
        selected ? 'border-yokoso-red bg-yokoso-red/5' : 'border-gray-200 bg-white'
      }`}
    >
      <div
        className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
          selected ? 'border-yokoso-red bg-yokoso-red' : 'border-gray-300'
        }`}
      >
        {selected && <Check size={10} className="text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm font-semibold text-text-primary line-clamp-2 leading-tight">{title}</p>
        <p className="font-body text-xs text-text-secondary mt-0.5">{subtitle}</p>
      </div>
    </button>
  )
}

/** #1357 確認ステップの 1 行。ラベルと値だけの単純な行。 */
function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
      <span className="font-body text-xs text-text-secondary shrink-0">{label}</span>
      <span className="font-body text-sm text-text-primary text-right truncate">{value}</span>
    </div>
  )
}
